/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createDefaultGridModel, ROW_DIRTY_CLASS } from '@gh/common-internal-shared';
import { getRouteInitData, queryFormEditable, State, TextTypes } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableCheckboxColumn,
  buildEditableTextColumn,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  rowDragMoveEventArrayFactory,
} from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { negate } from 'lodash';
import { Observable } from 'rxjs';
import {
  getJobCsDetailsSubheadingId,
  JobCsDetailsHeading,
  JobCsDetailsSubheading,
  jobCsDetailsSubheadingFieldState,
  validateJobCsDetailsSubheadingField,
} from '../../../resources/job-cs-details.model';
import { JobCsDetailsCommandService } from '../job-cs-details-command.service';
import {
  JobCsDetailsState,
  queryJobCsCurrentSubheadings,
  queryJobCsFocusedSubheadingCell,
  queryJobCsSelectedHeadingId,
  queryJobCsSelectedSubheading,
  queryJobCsSelectedSubheadingId,
} from '../job-cs-details.state';

@Component({
  standalone: false,
  selector: 'gh-job-cs-details-subheading-list',
  templateUrl: 'job-cs-details-subheading-list.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCsDetailsSubheadingListComponent implements OnInit {
  subheadings$: Observable<JobCsDetailsSubheading[]>;
  selectedId$: Observable<Maybe<ID>>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  hasSelectedParent$: Observable<boolean>;
  memo$: Observable<string>;

  gridDefinition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private state: State<JobCsDetailsState>,
              private commands: JobCsDetailsCommandService,
              private gridDefinitionFactory: GridDefinitionFactory) {

  }

  get editable(): boolean {
    return this.state.project(queryFormEditable);
  }

  ngOnInit(): void {
    this.gridDefinition = this.buildGridDefinition();

    this.subheadings$ = this.state.select(queryJobCsCurrentSubheadings);
    this.selectedId$ = this.state.select(queryJobCsSelectedSubheadingId);
    this.focusedCell$ = this.state.select(queryJobCsFocusedSubheadingCell);
    this.hasSelectedParent$ = this.state.select(queryJobCsSelectedHeadingId).pipe(map$(negate(Boolean)));

    this.memo$ = this.state.select(queryJobCsSelectedSubheading)
      .pipe(map$((subheading) => subheading ? subheading.memo : ''));
  }

  onAdd(): void {
    this.commands.subheadingAdd();
  }

  onClone(heading: JobCsDetailsSubheading): void {
    this.commands.subheadingClone(heading);
  }

  onRemove(heading: JobCsDetailsSubheading): void {
    this.commands.subheadingRemove(heading);
  }

  onSelectedChanged(subheadingId: ID): void {
    this.commands.subheadingSelect(subheadingId);
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  onMemoChange(memo: string): void {
    const subheadingId = this.state.project(queryJobCsSelectedSubheadingId);
    if (subheadingId) {
      this.commands.setSubheadingMemo(subheadingId, memo);
    }
  }

  private moveSubheadings(from: JobCsDetailsSubheading, to: JobCsDetailsSubheading): void {
    this.commands.subheadingMove(from, to);
  }

  private buildGridDefinition(): LayoutBasedGridDefinition {
    const { subheadingGridLayout } = getRouteInitData(this.route);

    const model = createDefaultGridModel({
      editable: this.state.project(queryFormEditable),
      getId: getJobCsDetailsSubheadingId,
      validate: validateJobCsDetailsSubheadingField,
      getCellDef: jobCsDetailsSubheadingFieldState,
      command: (entity, name, value) =>
        this.commands.setSubheadingField(getJobCsDetailsSubheadingId(entity), name, value),
    });

    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      options: <any>{
        rowDragEntireRow: true,
        rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
        defaultColDef: {
          sortable: false,
        },
        animateRows: true,
        onRowDragMove: rowDragMoveEventArrayFactory(this.moveSubheadings.bind(this)),
        rowClassRules: { [ROW_DIRTY_CLASS]: ({ data }: { data: JobCsDetailsHeading }) => data.isDirty },
      },
      columns: {
        name: {
          ...buildEditableTextColumn(TextTypes.Text40, model.getCellAccessor('name')),
          rowDrag: this.editable,
        },
        isHidden: buildEditableCheckboxColumn(model.getCellAccessor('isHidden')),
      },
      model,
      layout: subheadingGridLayout,
    });
  }
}
